/* eslint-disable no-param-reassign */
import { Dispatch, MutableRefObject, SetStateAction, useContext } from "react";
import { Box, Button, SxProps, Typography, CircularProgress } from "@mui/material";
import { SuccessAlertContext } from "App";
import { Search , HelpOutline } from "@mui/icons-material";
import { getUserType } from "util/auth";
import { SupplierStock } from "../types";
import Surround from "./Surround";
import downloadCountCsvUrl from "../api/downloadCountCsvUrl";
import downloadThresholdCsvUrl from "../api/downloadThresholdCsvUrl";
import downloadHelpPdf from "../api/downloadHelpPdf";

const styles: {[key: string]: SxProps} = {
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    position: "relative",
  },
  leftButtonContainer: {
    position: "absolute",
    top: 0,
    left: 0,
  },
};

type Props = {
  stockTitle: string;
  StockTable: JSX.Element;
  StockSearch: JSX.Element;
  setIsOpenStockSearch: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  onUpdate?: Function; // 仕入先・外部倉庫在庫数量アップデート
  changedStockList?: SupplierStock[]; // 仕入先・外部倉庫在庫の更新レコード
  isReadonly?: boolean; // 仕入先・外部倉庫在庫の更新画面か閲覧画面かを切り替える
  onChangeReadonlyScreen?: Function; // 仕入先・外部倉庫在庫の更新画面か閲覧画面かを切り替えたときの処理
  onOpenProductScreen?: Function; // 商品管理部用在庫一覧画面から更新を検索するモーダルを出す処理
  supplierCd?: string;
  storehouseCd?: string; // 仕入先・外部倉庫で使用する倉庫コード
  csvDownloading?: MutableRefObject<boolean>;
  setIsOpenStockCsvUpload?: Dispatch<SetStateAction<boolean>>;
  setIsOpenThresholdCsvUpload?: Dispatch<SetStateAction<boolean>>;
  onBack: Function;
  manualName: string;
};


/**
 * 在庫のコンテナコンポーネント
 * @param param0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function StockContainer({
  stockTitle,
  StockTable,
  StockSearch,
  setIsOpenStockSearch,
  isLoading,
  onUpdate,
  changedStockList,
  isReadonly,
  onChangeReadonlyScreen,
  onOpenProductScreen,
  supplierCd,
  storehouseCd,
  csvDownloading,
  setIsOpenStockCsvUpload,
  setIsOpenThresholdCsvUpload,
  onBack,
  manualName,
}: Props) {
  const { showAlert } = useContext(SuccessAlertContext);

  const downloadCountCsv = async () => {
    if (csvDownloading) {
      try {
        csvDownloading.current = true;
        await downloadCountCsvUrl(supplierCd || (storehouseCd ?? ''));
        showAlert('在庫数CSVをダウンロードしました。');
      } catch (err) {
        console.error(err);
      } finally {
        csvDownloading.current = false;
      }
    }
  };

  const downloadThresholdCsv = async () => {
    if (csvDownloading) {
      try {
        csvDownloading.current = true;
        await downloadThresholdCsvUrl(supplierCd ?? '');
        showAlert('設定値CSVをダウンロードしました。');
      } catch (err) {
        console.error(err);
      } finally {
        csvDownloading.current = false;
      }
    }
  };

  return (
    <Box width="90%" alignSelf="center" marginLeft="5%" minHeight={330} position="relative" pt={0}>
      <Box width="100%" height={32} textAlign="right">
        <Button variant="custom-outlined" onClick={async (e) => {
          const url = await downloadHelpPdf(`${getUserType()}/resources/stocks/${manualName}.pdf`);
          window.open(url, "_blank", "noopener,noreferrer");
        }} startIcon={<HelpOutline color="secondary" />} sx={{height: 24, padding: 1, marginTop: 0.5, marginRight: 1}}>ヘルプ</Button>
      </Box>
      <Box>
        <Box position="absolute" top={32} left={0} width="100%" textAlign="center">
          <Typography fontSize={22}>{stockTitle}</Typography>
        </Box>

        <Box sx={styles.buttonContainer}>
          { onBack &&
            <Box sx={styles.leftButtonContainer}>
              <Box mx={1}>
                <Button onClick={() => onBack()} variant="custom-outlined">
                  <Box>＜ 戻る</Box>
                </Button>
              </Box>
            </Box>
          }
          <Box mx={1}>
            <Button onClick={() => setIsOpenStockSearch(true)} variant="contained">
              <Search />
              <Box>絞込検索</Box>
            </Button>
          </Box>
          {/* { onOpenProductScreen &&
            <Box mx={1}>
              <Button onClick={() => onOpenProductScreen()} variant="contained">
                <Box>在庫数更新画面へ</Box>
              </Button>
            </Box>
          } */}
          { onUpdate && onChangeReadonlyScreen &&
            <>
              <Box mx={1}>
                <Button onClick={() => onChangeReadonlyScreen()} variant="contained">
                  <Box>{`${isReadonly ? '更新' : '閲覧'}画面に切替`}</Box>
                </Button>
              </Box>
              <Box mx={1}>
                <Button onClick={() => onUpdate()} variant="contained" disabled={changedStockList!.length === 0}>
                  <Box>数量を更新</Box>
                </Button>
              </Box>
            </>
          }
        </Box>
        { onUpdate && onChangeReadonlyScreen && setIsOpenStockCsvUpload && setIsOpenThresholdCsvUpload &&
          <Box sx={{...styles.buttonContainer, ...{position: "relative"}}}>
            <Surround title="CSV" sx={{...{display: "flex", flexDirection: "row"}, ...(!supplierCd ? {mt: 1.5} : {})}}>
              <Box m={1}>
                <Button onClick={() => downloadCountCsv()} variant="contained">
                  <Box>在庫数CSVダウンロード</Box>
                </Button>
              </Box>
              { !isReadonly &&
                <Box m={1}>
                  <Button onClick={() => setIsOpenStockCsvUpload(true)} variant="contained">
                    <Box>在庫数CSVアップロード</Box>
                  </Button>
                </Box>
              }
              { supplierCd &&
                <>
                  <Box m={1}>
                    <Button onClick={() => {downloadThresholdCsv()}} variant="contained">
                      <Box>設定値CSVダウンロード</Box>
                    </Button>
                  </Box>
                  { !isReadonly &&
                    <Box m={1}>
                      <Button onClick={() => setIsOpenThresholdCsvUpload(true)} variant="contained">
                        <Box>設定値CSVアップロード</Box>
                      </Button>
                    </Box>
                  }
                </>
              }
            </Surround>
          </Box>
        }
        { !isLoading &&
          <>
            { StockTable }
            { StockSearch }
          </>
        }
        { isLoading &&
          <Box height={330} textAlign="center" alignContent="center">
            <CircularProgress />
          </Box>
        }
      </Box>
    </Box>
  );
}
